import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Overlay from "../../../components/Overlay";
import Button from "../../../components/common/Button";
import * as Tooltip from "react-tooltip";
import { Field, Form, Formik } from "formik";
import { ReactComponent as VerifiedDocumentSvg } from "../../../assets/tendering/verified-document.svg";
import { ReactComponent as MessageSvg } from "../../../assets/profile/message.svg";
import { ReactComponent as ChevronDownSvg } from "../../../assets/icons/arrow-down-thin.svg";
import { ReactComponent as DownloadSvg } from "../../../assets/tendering/download.svg";
import useCommon from "../../../hooks/useCommon";
import AppContext from "../../../AppContext";
import ChatSessionContext from "../../../contexts/ChatSessionContext";
import useQuotation from "../../../hooks/useQuotation";
import { format } from "date-fns";
import useTendering from "../../../hooks/useTendering";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import _ from "lodash";

const assetsUri = import.meta.env.ASSETS_URI;

const ParticipantRow = ({ onWinnerSelected, ..._data }) => {
  const { id: tenderingId } = useParams();
  const { user } = useContext(AppContext);
  const { startChat } = useContext(ChatSessionContext);
  const { queryQuotation } = useQuotation();
  const { rejectParticipant, requestQuotation, requestSchedule, selectWinner } =
    useTendering();
  const [fetchingData, setFetchingData] = useState(true);
  const [isDocumentToggled, setIsDocumentToggled] = useState(false);
  const [isScheduleToggled, setIsScheduleToggled] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [data, setData] = useState(_data);

  const { rejected, round, quotation, schedule, user: _user } = data;

  const _getStatus = (data) => {
    const { rejected, quotation, schedule } = data;

    if (rejected) {
      return {
        label: "Rejected",
        color: "#fff",
        background: "red",
      };
    }

    if (quotation.requested && schedule.requested) {
      return {
        label: "Waiting documents",
        color: "#A66A01",
        background: "#FFDB4F",
      };
    }

    if (quotation.requested) {
      return {
        label: "Waiting quotation",
        color: "#A66A01",
        background: "#FFDB4F",
      };
    }

    if (schedule.requested) {
      return {
        label: "Waiting schedule",
        color: "#A66A01",
        background: "#FFDB4F",
      };
    }

    return {
      label: "Documents submitted",
      color: "#fff",
      background: "#18c67dff",
    };
  };

  const _status = _getStatus(data);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const {
      quotation: { submissions },
    } = data;

    if (submissions.length === 0) {
      setFetchingData(false);
    }

    // * Get quotations
    const { data: _quotations } = await queryQuotation(
      { _id: { $in: submissions }, active: true, deleted: false },
      null,
      -1
    );

    setQuotations(_quotations);
    setFetchingData(false);
  };

  const handleRequestQuotation = async () => {
    const result = await requestQuotation(tenderingId, _user._id);

    if (!result || !result.requested) return;

    setData((curr) => ({ ...curr, round: curr.round + 1, quotation: result }));

    toast.success("Requested new Quotation");
  };

  const handleRequestSchedule = async () => {
    const result = await requestSchedule(tenderingId, _user._id);

    if (!result || !result.requested) return;

    setData((curr) => ({ ...curr, schedule: result }));

    toast.success("Requested new Schedule");
  };

  const handleReject = async () => {
    const result = await rejectParticipant(tenderingId, _user._id);

    if (!result) {
      toast.error("Unable to reject");
      return;
    }

    setData((curr) => ({ ...curr, rejected: true, rejectedOn: new Date() }));

    toast.success("Rejected Participant");
  };

  const handleSelectWinner = async () => {
    const confirm = window.confirm("Select winner?");

    if (!confirm) {
      return;
    }

    await selectWinner(tenderingId, _user._id);
    onWinnerSelected();
  };

  const handleDownloadFile = async (fileName, path) => {
    await toast.promise(
      new Promise(async (resolve, reject) => {
        const res = await fetch(path, { cache: "no-cache" });
        const blob = await res.blob();

        saveAs(blob, fileName);

        resolve();
      }),
      {
        pending: "Downloading",
        success: "File downloaded",
        error: "Failed to download",
      },
      {
        autoClose: 1000,
      }
    );
  };

  let _documentSectionClass,
    _scheduleSectionClass =
      "tendering-detail__quotations__content__participant__content__documents";

  if (isDocumentToggled) {
    _documentSectionClass +=
      " tendering-detail__quotations__content__participant__content__documents--toggled";
  }

  if (isScheduleToggled) {
    _scheduleSectionClass +=
      " tendering-detail__quotations__content__participant__content__documents--toggled";
  }

  if (fetchingData) return null;

  return (
    <div className="tendering-detail__quotations__content__participant">
      <div className="tendering-detail__quotations__content__participant__header">
        <div className="tendering-detail__quotations__content__participant__header__top">
          <div className="tendering-detail__quotations__content__participant__info">
            <img
              className="tendering-detail__quotations__content__participant__info__avatar"
              src={`${assetsUri}/${_user.avatar}`}
            />
            <span className="tendering-detail__quotations__content__participant__info__name">
              {_user.name}
            </span>
            <div
              className="tendering-detail__quotations__content__participant__info__chat"
              onClick={() => {
                startChat(_user._id, user._id);
              }}
            >
              <MessageSvg width="20" height="20" />
              Chat
            </div>
          </div>
          <div className="tendering-detail__quotations__content__participant__status">
            <span
              className="tendering-detail__quotations__content__participant__status__current"
              style={{
                color: _status.color,
                background: _status.background,
              }}
            >
              {_status.label}
            </span>
            <span className="tendering-detail__quotations__content__participant__status__round">
              Round {round}
            </span>
          </div>
        </div>
        <div className="tendering-detail__quotations__content__participant__header__bottom">
          <div className="tendering-detail__quotations__content__participant__actions">
            <div
              className={`tendering-detail__quotations__content__participant__actions__action ${
                rejected || quotation.requested
                  ? "tendering-detail__quotations__content__participant__actions__action--disabled"
                  : ""
              }`}
              onClick={handleRequestQuotation}
            >
              <VerifiedDocumentSvg height={18} />
              Request new Quotation
            </div>
            <div className="tendering-detail__quotations__content__participant__actions__divider" />
            <div
              className={`tendering-detail__quotations__content__participant__actions__action ${
                rejected || schedule.requested
                  ? "tendering-detail__quotations__content__participant__actions__action--disabled"
                  : ""
              }`}
              onClick={handleRequestSchedule}
            >
              <VerifiedDocumentSvg height={18} />
              Request Master Schedule
            </div>
          </div>
        </div>
      </div>
      <div className="tendering-detail__quotations__content__participant__content">
        <div className={_documentSectionClass}>
          <span
            className="tendering-detail__quotations__content__participant__content__documents__title"
            onClick={() => setIsDocumentToggled(!isDocumentToggled)}
          >
            Quotations
            <ChevronDownSvg
              className="tendering-detail__quotations__content__participant__content__documents__title__arrow"
              width="20"
            />
          </span>
          {isDocumentToggled && (
            <div className="tendering-detail__quotations__content__participant__content__documents__list">
              {quotations.length === 0 && (
                <div className="tendering-detail__quotations__content__participant__content__documents__list__item">
                  Contractor hasn't provided any quotations yet
                </div>
              )}
              {quotations.map((q) => {
                return (
                  <div
                    className="tendering-detail__quotations__content__participant__content__documents__list__item"
                    key={q.code}
                  >
                    <span className="tendering-detail__quotations__content__participant__content__documents__list__item__name">
                      {q.code}
                    </span>
                    <span className="tendering-detail__quotations__content__participant__content__documents__list__item__date">
                      {format(new Date(q.createdOn), "dd MMM yyyy")}
                    </span>
                    <DownloadSvg
                      className="tendering-detail__quotations__content__participant__content__documents__list__item__download"
                      onClick={() =>
                        handleDownloadFile(
                          q.file.name,
                          `${assetsUri}/${q.file.key}`
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className={_scheduleSectionClass}>
          <span
            className="tendering-detail__quotations__content__participant__content__documents__title"
            onClick={() => setIsScheduleToggled(!isScheduleToggled)}
          >
            Master Schedules
            <ChevronDownSvg
              className="tendering-detail__quotations__content__participant__content__documents__title__arrow"
              width="20"
            />
          </span>
          {isScheduleToggled && (
            <div className="tendering-detail__quotations__content__participant__content__documents__list">
              {schedule.files.length === 0 && (
                <div className="tendering-detail__quotations__content__participant__content__documents__list__item">
                  Contractor hasn't provided any schedules yet
                </div>
              )}
              {schedule.files.map((schedule, scheduleIndex) => {
                return (
                  <div
                    className="tendering-detail__quotations__content__participant__content__documents__list__item"
                    key={`schedule-${scheduleIndex}`}
                  >
                    <span className="tendering-detail__quotations__content__participant__content__documents__list__item__name">
                      {schedule.name}
                    </span>
                    <span className="tendering-detail__quotations__content__participant__content__documents__list__item__date">
                      {format(new Date(schedule.createdOn), "dd MMM yyyy")}
                    </span>
                    <DownloadSvg
                      className="tendering-detail__quotations__content__participant__content__documents__list__item__download"
                      onClick={() =>
                        handleDownloadFile(
                          schedule.name,
                          `${assetsUri}/${schedule.key}`
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="tendering-detail__quotations__content__participant__footer">
        <Button
          label={"Reject"}
          variant={"secondary"}
          size={"small"}
          disabled={rejected}
          onClick={handleReject}
        />
        <Button
          label={"Select as winner"}
          size={"small"}
          disabled={rejected}
          onClick={handleSelectWinner}
        />
      </div>
    </div>
  );
};

const Quotations = ({
  tenderingId,
  onClose,
  participants,
  onWinnerSelected,
}) => {
  const [init, setInit] = useState(false);
  const [datasource, setDatasource] = useState([]);
  const { getUsersAndCompanies } = useCommon();

  useEffect(() => {
    if (init) return;

    // * Handle rendering boqs
    let _datasource = _.cloneDeep(participants).filter(
      (p) => p.accepted === true
    );

    const _ids = _datasource.map((data) => data.user ?? data.company);

    setInit(true);
    getUsersAndCompanies(_ids).then((result) => {
      setDatasource(
        _datasource.map((data) => {
          let _user = result.find(
            (r) => r._id === data.user || r._id === data.company
          );

          data.user = _user;

          return data;
        })
      );
    });

    return () => {};
  }, [init, participants]);

  return (
    <Overlay onClose={onClose}>
      <div className="tendering-detail__quotations">
        <section className="tendering-detail__quotations__header">
          <span className="tendering-detail__quotations__header__title">
            Quotations
          </span>
        </section>
        <div className="tendering-detail__quotations__content__scroller">
          <Formik
            initialValues={{ winner: undefined }}
            validate={(values) => console.log(values)}
          >
            <Form className="tendering-detail__quotations__content">
              {datasource.map((data) => {
                return (
                  <ParticipantRow
                    {...data}
                    onWinnerSelected={onWinnerSelected}
                  />
                );
              })}
            </Form>
          </Formik>
        </div>
        <div
          className="tendering-detail__quotations__footer"
          data-tooltip-id="status-hint"
          // data-tooltip-content={
          //   !canContinue ? "Too many or no participants have been selected" : ""
          // }
        >
          {/* <Button
              label="Confirm participants and continue"
              width={"100%"}
              disabled={!canContinue}
              onClick={() => {
                update(tenderingId, { status: 2 }).then((result) => {
                  if (result.status !== 2) {
                    alert("error");
                    return;
                  }
  
                  onChange({
                    status: 2,
                  });
                });
              }}
            /> */}
        </div>
      </div>
      <Tooltip.Tooltip id="status-hint" />
    </Overlay>
  );
};

Quotations.propTypes = {};

export default Quotations;
